import { InfoCircleOutlined, LogoutOutlined, SettingOutlined, ToolOutlined, UserOutlined } from '@ant-design/icons';
import { useDecaf } from '@decafhub/decaf-react';
import { Avatar, Dropdown } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProfileDropdownItem } from '../types';
import { followHref, getGravatarUrl, logout } from '../utils';

export interface UserProfileDropdownProps {
  /** Consumer-contributed items inserted between the built-in Maestro entry and the logout block. */
  items?: UserProfileDropdownItem[];
}

/**
 * Derives the Maestro href from the current path.
 *
 * Decaf webapps are deployed under `/webapps/<app>/<env>/...`. The env is a
 * frontend-only deployment concern (staging and production share the same
 * backend), so this is purely a UX nicety: staging-app users land on
 * staging-Maestro, everyone else (production, local dev, anything we can't
 * classify) lands on production-Maestro.
 */
function getMaestroHref(): string {
  const env = window.location.pathname.split('/')[3];
  return env === 'staging' ? '/webapps/maestro/staging/' : '/webapps/maestro/production/';
}

export function UserProfileDropdown({ items: extraItems }: UserProfileDropdownProps = {}) {
  const [gravatar, setGravatar] = useState<string | undefined>(undefined);
  const decaf = useDecaf();
  const navigate = useNavigate();

  useEffect(() => {
    if (!decaf.me.email) {
      return;
    }

    getGravatarUrl(decaf.me.email).then(setGravatar);
  }, [decaf.me.email]);

  const doLogout = () => logout(decaf.client, false);
  const doLogoutAll = () => window.confirm('Logout from all sessions?') && logout(decaf.client, true);
  const doGoToAbout = () => navigate('/about');
  const doGoToSettings = () => (window.location.href = `/webapps/waitress/production/profile`);

  const showMaestro = decaf.me.privileged;
  const doGoToMaestro = () => window.open(getMaestroHref(), '_blank', 'noopener,noreferrer');

  const builtInItems: ItemType[] = [
    {
      key: 'me',
      label: decaf.me.fullname || decaf.me.username,
      title: decaf.me.email,
      disabled: true,
      icon: <UserOutlined />,
      style: { cursor: 'default' },
    },
    {
      key: 'settings',
      label: 'Settings',
      title: 'User Settings',
      icon: <SettingOutlined />,
      onClick: doGoToSettings,
    },
    { key: 'about', label: 'About', icon: <InfoCircleOutlined />, onClick: doGoToAbout },
  ];

  if (showMaestro) {
    builtInItems.push({
      key: 'maestro',
      label: (
        <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <span>Open Maestro</span>
          <span style={{ fontSize: 11, opacity: 0.6 }}>Admin application</span>
        </span>
      ),
      title: 'Open Maestro in a new tab',
      icon: <ToolOutlined />,
      onClick: doGoToMaestro,
    });
  }

  const consumerItems: ItemType[] = (extraItems ?? []).map((item) => toAntdItem(item, navigate));

  const items: ItemType[] = [
    ...builtInItems,
    ...consumerItems,
    { key: 'divider-1', dashed: true, type: 'divider' },
    { key: 'logout', label: 'Logout', icon: <LogoutOutlined />, onClick: doLogout, style: { color: 'orange' } },
    {
      key: 'logout-all',
      label: 'Logout All Sessions',
      icon: <LogoutOutlined />,
      onClick: doLogoutAll,
      danger: true,
    },
  ];

  const propsAvatar: any = gravatar
    ? { src: gravatar }
    : {
        icon: <UserOutlined style={{ color: 'rgba(255,255,255, 0.5)' }} />,
        style: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255, 0.5)' },
      };

  return (
    <Dropdown trigger={['click']} menu={{ items }}>
      <Avatar {...propsAvatar} style={{ ...propsAvatar.style, cursor: 'pointer' }} />
    </Dropdown>
  );
}

function toAntdItem(item: UserProfileDropdownItem, navigate: ReturnType<typeof useNavigate>): ItemType {
  if ('type' in item && item.type === 'divider') {
    return { key: item.key, type: 'divider', dashed: item.dashed };
  }

  const action = item as Exclude<UserProfileDropdownItem, { type: 'divider' }>;
  const onClick = (() => {
    if ('to' in action && action.to !== undefined) {
      const to = action.to;
      return () => navigate(to);
    }
    if ('href' in action && action.href !== undefined) {
      const href = action.href;
      return () => followHref(href);
    }
    return action.onClick;
  })();

  return {
    key: action.key,
    label: action.label,
    icon: action.icon,
    disabled: action.disabled,
    danger: action.danger,
    onClick,
  };
}
